import { createContext, useContext, useEffect, useState } from "react"
import { jwtDecode } from "jwt-decode"
import { apiClient } from "@/api/client"
import { AuthResponse, LoginRequest, RegisterRequest, RegisterResponse, UserType } from "@/api/api-client"

export { UserType }

interface DecodedToken {
  nameid: string
  email: string
  displayName: string
  userType: string // Comes as string from backend: "Vendor", "Buyer", etc.
  exp: number
}

export interface AuthUser {
  id: string
  email: string
  displayName: string
  userType: UserType
  role: string // Lowercase string for convenience: "vendor", "buyer", "admin"
}

interface AuthContextType {
  user: AuthUser | null
  token: string | null
  loading: boolean
  login: (req: LoginRequest) => Promise<void>
  register: (req: RegisterRequest) => Promise<RegisterResponse>
  externalLogin: (provider: string, idToken: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const getInitialAuthState = (): { user: AuthUser | null; token: string | null } => {
    if (typeof window === "undefined") return { user: null, token: null }
    
    const savedToken = localStorage.getItem("meatbid_token")
    if (!savedToken) return { user: null, token: null }

    try {
      const decoded = jwtDecode<DecodedToken>(savedToken)
      
      // Check if token is expired
      if (decoded.exp * 1000 < Date.now()) {
        localStorage.removeItem("meatbid_token")
        return { user: null, token: null }
      }

      const userType = UserType[decoded.userType as keyof typeof UserType]
      return {
        token: savedToken,
        user: {
          id: decoded.nameid,
          email: decoded.email,
          displayName: decoded.displayName,
          userType: userType,
          role: decoded.userType.toLowerCase(),
        }
      }
    } catch (error) {
      localStorage.removeItem("meatbid_token")
      return { user: null, token: null }
    }
  }

  const [authState, setAuthState] = useState<{ user: AuthUser | null; token: string | null }>(getInitialAuthState)
  const [loading, setLoading] = useState(false) // No longer need to start as true since we sync on init

  const user = authState.user
  const token = authState.token

  const handleAuthResponse = (response: AuthResponse) => {
    const { token, email, displayName } = response
    localStorage.setItem("meatbid_token", token)

    const decoded = jwtDecode<DecodedToken>(token)
    const userType = UserType[decoded.userType as keyof typeof UserType]

    const authUser: AuthUser = {
      id: decoded.nameid,
      email: email,
      displayName: displayName,
      userType: userType,
      role: decoded.userType.toLowerCase(),
    }

    setAuthState({ token, user: authUser })
  }

  const login = async (req: LoginRequest) => {
    const response = await apiClient.login(req)
    handleAuthResponse(response)
  }

  const register = async (req: RegisterRequest) => {
    return await apiClient.register(req)
  }

  const externalLogin = async (provider: string, idToken: string) => {
    const response = await apiClient.externalLogin({ provider, idToken })
    handleAuthResponse(response)
  }

  const logout = () => {
    localStorage.removeItem("meatbid_token")
    setAuthState({ user: null, token: null })
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, externalLogin, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

